import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import useIsLoading from "../../../hooks/useIsLoading";
import {
  getFactoringProvidersForEezyCompany,
  PaymentTerm,
} from "../../invoices/invoiceAPI";
import { getOrderQueues, OrderQueue } from "../../orders/orderAPI";
import { handleSalesAccountsData } from "../../settings/helpers/utils";
import { SalesAccount } from "../../settings/settingsApi";
import {
  fetchExpertUsers,
  selectActiveExpertUserOptions,
} from "../../user/expertUsersSlice";
import {
  fetchPaymentTerms,
  fetchSalesAccounts,
} from "../customerContractsSlice";
import { selectAllCustomerOrganizations } from "../customerOrganizationsSlice";
import {
  fetchCustomerUsers,
  fetchCustomerUsersByCompanyId,
  selectAllCustomerUsers,
} from "../customerUsersSlice";
import { CustomerOrganizationsResult, CustomerUser } from "../types/queries";
import { getRepresentativeAndSubOrganizations } from "./helpers/contractHelpers";
import { NameIdOption } from "../../../common/types/commonTypes";

// Define the data context type
interface DataContextType {
  allCustomerOrganizations: CustomerOrganizationsResult[];
  representativeCustomerOrganization: CustomerOrganizationsResult | null;
  subCustomerOrganizations: CustomerOrganizationsResult[];
  customerUsers: CustomerUser[];
  expertUserOptions: NameIdOption[];
  orderQueues: OrderQueue[];
  paymentTermOptions: PaymentTerm[];
  salesAccountsOptions: SalesAccount[];
  salesAccountsObject: any;
  factoringProviderOptions: any[];
}

// Create the ContractDataContext with default values
const ContractDataContext = createContext<DataContextType | undefined>(
  undefined,
);

// Custom hook to use the ContractDataContext
export const useContractDataContext = (): DataContextType => {
  const context = useContext(ContractDataContext);
  if (!context) {
    throw new Error(
      "useContractDataContext must be used within a DataProvider",
    );
  }
  return context;
};

interface Props {
  children: React.ReactElement;
}

const ContractDataProvider = ({ children }: Props) => {
  const { t } = useTranslation("customers");
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const eezyCompanyId = searchParams.get("ecom");
  const { customerCompanyId } = useParams();

  // Data states
  const [orderQueues, setOrderQueues] = useState<OrderQueue[]>([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState<PaymentTerm[]>(
    [],
  );
  const [salesAccountsOptions, setSalesAccountsOptions] = useState<
    SalesAccount[]
  >([]);
  const [salesAccountsObject, setSalesAccountsObject] = useState<any>({});
  const [factoringProviderOptions, setFactoringProviderOptions] = useState<
    any[]
  >([]);
  const customerUsers = useAppSelector((state) =>
    selectAllCustomerUsers(state),
  );
  const expertUserOptions = useAppSelector((state) => selectActiveExpertUserOptions(state));
  // Customer organizations are fetched in CustomerManagement.tsx
  // We can just access them from the store instead of fetching them again
  const allCustomerOrganizations = useAppSelector((state) =>
    selectAllCustomerOrganizations(state),
  ).filter((org) => org.customerCompanyId === customerCompanyId);
  const {
    repOrg: representativeCustomerOrganization,
    subOrgs: subCustomerOrganizations,
  } = getRepresentativeAndSubOrganizations(allCustomerOrganizations);

  // Loading states
  const [orderQueuesLoading, setOrderQueuesLoading] = useState<boolean>(true);
  const [paymentTermsLoading, setPaymentTermsLoading] = useState<boolean>(true);
  const [salesAccountsLoading, setSalesAccountsLoading] =
    useState<boolean>(true);
  const [factoringProvidersLoading, setFactoringProvidersLoading] =
    useState<boolean>(true);
  const usersLoading = useIsLoading(["customerUsers", "expertUsers"]);

  // Check if any of the data is loading
  const isLoading =
    orderQueuesLoading ||
    paymentTermsLoading ||
    salesAccountsLoading ||
    factoringProvidersLoading ||
    usersLoading;

  // Fetch expert users for contract options
  useEffect(() => {
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  // Fetch customer users for contract options
  useEffect(() => {
    if (customerCompanyId) {
      dispatch(fetchCustomerUsersByCompanyId({ customerCompanyId }));
    } else {
      dispatch(fetchCustomerUsers({}));
    }
  }, [dispatch, customerCompanyId]);

  // Get order queues for contract form
  useEffect(() => {
    getOrderQueues()
      .then((res) => setOrderQueues(res.data))
      .finally(() => {
        setOrderQueuesLoading(false);
      });
  }, []);

  // Fetch payment terms
  useEffect(() => {
    dispatch(fetchPaymentTerms({}))
      .then((action) => {
        if (isFulfilled(action)) {
          setPaymentTermOptions(action.payload.paymentTerms);
        }
      })
      .finally(() => {
        setPaymentTermsLoading(false);
      });
  }, [dispatch]);

  // Fetch sales accounts
  useEffect(() => {
    const currentDate = format(new Date(), isoFormat);
    dispatch(fetchSalesAccounts({ validFrom: currentDate }))
      .then((action) => {
        if (isFulfilled(action)) {
          const { accounts } = action.payload;
          setSalesAccountsObject(accounts);
          const accountsArray = handleSalesAccountsData(accounts);
          setSalesAccountsOptions(
            accountsArray.filter((account) => account.canUseInContract),
          );
        }
      })
      .finally(() => {
        setSalesAccountsLoading(false);
      });
  }, [dispatch]);

  // Fetch factoring providers
  useEffect(() => {
    if (!eezyCompanyId) return;
    getFactoringProvidersForEezyCompany(eezyCompanyId)
      .then((res) => {
        const { factoringProviders } = res.data;
        const options = [
          { value: "", label: t("contract.factoringNotSelected") },
          ...factoringProviders?.map((provider) => ({
            value: provider.value.factoringContractId,
            label: provider.value.name,
          })),
        ];
        setFactoringProviderOptions(options);
      })
      .finally(() => {
        setFactoringProvidersLoading(false);
      });
  }, [eezyCompanyId, t]);

  if (isLoading) {
    return <LoadingSpinner sx={{ height: "100px" }} />;
  }

  return (
    <ContractDataContext.Provider
      value={{
        allCustomerOrganizations,
        representativeCustomerOrganization,
        subCustomerOrganizations,
        customerUsers,
        expertUserOptions,
        orderQueues,
        paymentTermOptions,
        salesAccountsOptions,
        salesAccountsObject,
        factoringProviderOptions,
      }}
    >
      {children}
    </ContractDataContext.Provider>
  );
};

export default ContractDataProvider;
